import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F2] p-6">
        <div className="max-w-md w-full bg-white border border-border rounded-2xl p-8 shadow-sm text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-orange-50 flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mb-6">
            An unexpected error occurred. Our team has been notified. Try refreshing the page.
          </p>
          <Button onClick={this.handleReset} className="w-full">Refresh page</Button>
        </div>
      </div>
    );
  }
}
